
import html2canvas from 'html2canvas';
import { AiAnalysisSession } from '@/services/AiMarketAnalysisService';

export class ScreenshotExportService {
  private static addWatermark(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Add watermark at bottom right
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'; // White text for dark backgrounds
    ctx.textAlign = 'right';
    ctx.fillText('Trade Bot 365', canvas.width - 20, canvas.height - 20);

    // Add timestamp
    const now = new Date().toLocaleString();
    ctx.font = '12px Arial';
    ctx.fillText(now, canvas.width - 20, canvas.height - 40);

    return canvas;
  }

  private static downloadCanvas(canvas: HTMLCanvasElement, fileName: string) {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private static getActualBackgroundColor(): string {
    // Get the actual background color from the page
    const bodyStyle = window.getComputedStyle(document.body);
    const htmlStyle = window.getComputedStyle(document.documentElement);
    
    // Check for dark theme indicators
    const isDarkTheme = document.documentElement.classList.contains('dark') || 
                       bodyStyle.backgroundColor.includes('rgb(0') ||
                       htmlStyle.backgroundColor.includes('rgb(0');
    
    // Return the app's actual background colors
    if (isDarkTheme) {
      // Dark theme background color from CSS variables: hsl(240 10% 3.9%) ≈ #0a0a0b
      return '#0a0a0b';
    } else {
      // Light theme background color from CSS variables: hsl(0 0% 100%) = #ffffff
      return '#ffffff';
    }
  }

  static async exportAnalysisAsScreenshot(session: AiAnalysisSession, elementId?: string): Promise<void> {
    try {
      // Find the element to capture - default to the entire analysis content
      const targetElement = elementId 
        ? document.getElementById(elementId)
        : document.querySelector('[data-analysis-content]') || document.body;

      if (!targetElement) {
        throw new Error('Target element not found for screenshot');
      }

      // Scroll to top to ensure full content is visible
      window.scrollTo(0, 0);
      
      // Wait a bit for scroll to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get the actual background color
      const actualBgColor = this.getActualBackgroundColor();

      // Configure html2canvas options to preserve background
      const canvas = await html2canvas(targetElement as HTMLElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: actualBgColor, // Use actual app background color
        scale: 2, // Higher quality
        logging: false,
        height: targetElement.scrollHeight,
        width: targetElement.scrollWidth,
        scrollX: 0,
        scrollY: 0,
        ignoreElements: (element) => {
          // Skip elements that might interfere with background capture
          return element.classList.contains('ignore-screenshot');
        }
      });

      // Add watermark
      const watermarkedCanvas = this.addWatermark(canvas);

      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const fileName = `${session.symbol}_Analysis_Screenshot_${date}.png`;

      // Download
      this.downloadCanvas(watermarkedCanvas, fileName);
    } catch (error) {
      console.error('Screenshot export failed:', error);
      throw new Error('Failed to capture screenshot');
    }
  }

  static async exportMultipleAnalysesAsScreenshots(sessions: AiAnalysisSession[]): Promise<void> {
    try {
      for (let i = 0; i < sessions.length; i++) {
        const session = sessions[i];
        
        // For bulk export, we'll capture the current page if it matches the session
        // This is a simplified approach - in a real implementation, you might navigate to each page
        await this.exportAnalysisAsScreenshot(session);
        
        // Add delay between exports to prevent overwhelming the browser
        if (i < sessions.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } catch (error) {
      console.error('Bulk screenshot export failed:', error);
      throw new Error('Failed to export multiple screenshots');
    }
  }

  static async exportCurrentPageAsScreenshot(fileName?: string): Promise<void> {
    try {
      // Capture the main content area
      const contentElement = document.querySelector('main') || 
                           document.querySelector('[data-analysis-content]') || 
                           document.body;

      const actualBgColor = this.getActualBackgroundColor();

      const canvas = await html2canvas(contentElement as HTMLElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: actualBgColor, // Use actual app background color
        scale: 2,
        logging: false,
        scrollX: 0,
        scrollY: 0
      });

      const watermarkedCanvas = this.addWatermark(canvas);
      
      const defaultFileName = `Analysis_Screenshot_${new Date().toISOString().split('T')[0]}.png`;
      this.downloadCanvas(watermarkedCanvas, fileName || defaultFileName);
    } catch (error) {
      console.error('Current page screenshot failed:', error);
      throw new Error('Failed to capture current page screenshot');
    }
  }
}
